/**
 * P422 -- what the bank has told its regulator about this incident.
 *
 * Each stage (initial, update, final) is a record with a deadline taken
 * from the regulator's own rule and frozen when the stage is opened. The
 * deadline exists only where the regulator states hours: SAMA's framework
 * says "immediately" and DORA's time limits sit in technical standards
 * this deployment does not hold, so for those the panel prints what the
 * regulator does say rather than a clock it never set. MARSAD records
 * that a submission happened and its reference; it does not transmit.
 */
import { said } from "shared/i18n/said";
import { refusalMessage } from "shared/api/refusalMessage";
import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";

import { api, ApiError } from "shared/api/client";
import {
  NOTICE_FIELDS,
  type IncidentReport,
  type IncidentReporting,
  type NoticeField,
} from "shared/api/types";
import { formatDateTime } from "shared/time/formatDate";
import { useToast } from "shared/toast/ToastProvider";

const key = (findingId: string) => ["incident-reports", findingId] as const;

function StageRow({
  report,
  canRecord,
  findingId,
}: {
  report: IncidentReport;
  canRecord: boolean;
  findingId: string;
}) {
  const { t, i18n } = useTranslation();
  const qc = useQueryClient();
  const toast = useToast();
  const [reference, setReference] = useState("");
  const [issued, setIssued] = useState(false);
  const missing = report.missing_fields ?? [];
  const submit = useMutation<IncidentReport, ApiError, string>({
    mutationFn: (ref) =>
      api.post<IncidentReport>(`/v1/incident-reports/${report.id}/submission`, {
        reference: ref,
        reference_kind: issued ? "regulator_issued" : "bank_record",
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: key(findingId) });
      toast.push({
        tone: "success",
        message: t("modules.twin.incidentReporting.recorded"),
      });
    },
    onError: (err) => toast.push({ tone: "error", message: refusalMessage(t, err) }),
  });
  const p = "modules.twin.incidentReporting";
  return (
    <li
      data-testid="incident-report-stage"
      data-stage={report.stage}
      data-status={report.status}
      className="rounded border border-slate-200 px-3 py-2 text-sm"
    >
      <div className="flex flex-wrap items-center gap-2">
        <span className="font-medium">{t(`${p}.stage.${report.stage}`)}</span>
        <span
          className={[
            "rounded-full px-2 py-0.5 text-xs",
            report.status === "late"
              ? "bg-rose-50 text-rose-800"
              : report.status === "submitted"
                ? "bg-emerald-50 text-emerald-800"
                : "bg-slate-100 text-slate-700",
          ].join(" ")}
        >
          {t(`${p}.status.${report.status}`)}
        </span>
        <span data-testid="incident-report-due" className="text-slate-600">
          {report.due_at
            ? t(`${p}.dueAt`, { at: formatDateTime(report.due_at, i18n.language) })
            : t(`${p}.basis.${report.due_basis}`)}
        </span>
      </div>
      {report.submitted_at ? (
        <p className="mt-1 text-xs text-slate-600">
          {t(report.submitted_late ? `${p}.submittedLate` : `${p}.submitted`, {
            at: formatDateTime(report.submitted_at, i18n.language),
            reference: report.reference ?? "",
            kind:
              report.reference_kind === "regulator_issued"
                ? t(`${p}.referenceRegulatorIssued`)
                : t(`${p}.referenceBankRecord`),
          })}
        </p>
      ) : null}
      {report.stage === "initial" && report.content && (
        <NoticeContent
          report={report}
          editable={canRecord && !report.submitted_at}
          findingId={findingId}
        />
      )}
      {!report.submitted_at && canRecord && (
        <form
          className="mt-2 flex flex-wrap items-center gap-2"
          onSubmit={(e) => {
            e.preventDefault();
            if (reference.trim() && missing.length === 0)
              submit.mutate(reference.trim());
          }}
        >
          {missing.length > 0 && (
            <p
              data-testid="incident-notice-missing"
              className="w-full text-xs text-amber-800"
            >
              {t(`${p}.noticeMissing`, {
                fields: missing.map((f) => t(`${p}.noticeField.${f}`)).join(", "),
              })}
            </p>
          )}
          <label className="text-xs text-slate-600">
            {t(`${p}.reference`)}{" "}
            <input
              data-testid="incident-report-reference"
              value={reference}
              onChange={(e) => setReference(e.target.value)}
              className="ms-1 rounded border border-slate-300 px-2 py-0.5 text-sm"
            />
          </label>
          <label className="flex items-center gap-1 text-xs text-slate-600">
            <input
              type="checkbox"
              data-testid="incident-report-reference-issued"
              checked={issued}
              onChange={(e) => setIssued(e.target.checked)}
            />
            {t(`${p}.referenceIssued`)}
          </label>
          <button
            type="submit"
            data-testid="incident-report-record"
            disabled={!reference.trim() || missing.length > 0 || submit.isPending}
            className="rounded bg-brand-brass px-2 py-0.5 text-xs font-medium text-brand-navy hover:bg-brand-brassDark disabled:opacity-50"
          >
            {t(`${p}.record`)}
          </button>
        </form>
      )}
    </li>
  );
}

/** Round 20 U3 -- what the initial notice says, drafted from the record,
 *  editable until it is recorded as sent. */
function NoticeContent({
  report,
  editable,
  findingId,
}: {
  report: IncidentReport;
  editable: boolean;
  findingId: string;
}) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const toast = useToast();
  const p = "modules.twin.incidentReporting";
  const [draft, setDraft] = useState<Partial<Record<NoticeField, string>>>(() => ({
    ...(report.content ?? {}),
  }));
  const save = useMutation<IncidentReport, ApiError, void>({
    mutationFn: () =>
      api.patch<IncidentReport>(`/v1/incident-reports/${report.id}/content`, draft),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: key(findingId) });
      toast.push({ tone: "success", message: t(`${p}.noticeSaved`) });
    },
    onError: (err) => toast.push({ tone: "error", message: refusalMessage(t, err) }),
  });
  return (
    <div data-testid="incident-notice" className="mt-2 space-y-1 text-xs">
      <p className="font-medium text-slate-800">{t(`${p}.noticeHeading`)}</p>
      {editable && <p className="text-slate-500">{t(`${p}.noticeDraftedFrom`)}</p>}
      <dl className="space-y-1">
        {NOTICE_FIELDS.map((f) => (
          <div key={f}>
            <dt className="text-slate-600">{t(`${p}.noticeField.${f}`)}</dt>
            <dd>
              {editable ? (
                <textarea
                  dir="auto"
                  data-testid={`incident-notice-${f}`}
                  rows={f === "what_happened" ? 3 : 1}
                  value={draft[f] ?? ""}
                  onChange={(e) => setDraft((d) => ({ ...d, [f]: e.target.value }))}
                  className="w-full rounded border border-slate-300 px-2 py-1 text-sm"
                />
              ) : (
                <span data-testid={`incident-notice-${f}`} className="text-slate-800">
                  {report.content?.[f] ?? ""}
                </span>
              )}
            </dd>
          </div>
        ))}
      </dl>
      {editable && (
        <button
          type="button"
          data-testid="incident-notice-save"
          disabled={save.isPending}
          onClick={() => save.mutate()}
          className="rounded border border-brand-brass/40 bg-brand-brass/5 px-2 py-0.5 text-xs font-medium text-brand-brassDark hover:bg-brand-brass/10 disabled:opacity-50"
        >
          {t(`${p}.noticeSave`)}
        </button>
      )}
    </div>
  );
}

export function IncidentReportingPanel({
  findingId,
  canRecord,
}: {
  findingId: string;
  canRecord: boolean;
}) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const toast = useToast();
  const p = "modules.twin.incidentReporting";
  const q = useQuery<IncidentReporting, ApiError>({
    queryKey: key(findingId),
    queryFn: () =>
      api.get<IncidentReporting>(`/v1/findings/${findingId}/incident-reports`),
    retry: false,
  });
  const open = useMutation<IncidentReport, ApiError, string>({
    mutationFn: (stage) =>
      api.post<IncidentReport>(`/v1/findings/${findingId}/incident-reports`, { stage }),
    onSuccess: () => void qc.invalidateQueries({ queryKey: key(findingId) }),
    // Round 20 H59 -- a request that never reached the server read only
    // "Failed to fetch". It did nothing, and says so.
    onError: (err: unknown) =>
      toast.push({
        tone: "error",
        message:
          err instanceof ApiError ? refusalMessage(t, err) : t(`${p}.openUnreached`),
      }),
  });
  if (!q.data) return null;
  const data = q.data;
  const reports = Array.isArray(data.reports) ? data.reports : [];
  const has = (stage: string) => reports.some((r) => r.stage === stage);
  return (
    <section
      id="incident-reporting"
      data-testid="incident-reporting"
      data-regime={data.regime_code}
      className="space-y-2 rounded-lg border border-slate-200 bg-white p-4"
    >
      <h2 className="text-sm font-semibold text-slate-900">
        {t(`${p}.heading`, { regulator: data.regulator_short })}
      </h2>
      {data.profile ? (
        <p data-testid="incident-reporting-rule" className="text-xs text-slate-600">
          {t(`${p}.recipient`, {
            // Round 16 I14 -- the recipient in the reader's words; the
            // citation below quotes the regulator and stays as written.
            recipient: said(
              t,
              `enumWords.incidentRecipient.${data.regime_code ?? ""}`,
              data.profile.recipient_label,
            ),
          })}{" "}
          {data.profile.initial_deadline_hours
            ? t(`${p}.ruleHours`, { hours: data.profile.initial_deadline_hours })
            : t(`${p}.ruleBasis.${data.profile.initial_basis}`)}
        </p>
      ) : (
        <p
          data-testid="incident-reporting-unrecorded"
          className="text-xs text-amber-800"
        >
          {t(`${p}.unrecorded`, { regulator: data.regulator_short })}
        </p>
      )}
      {data.regime_source === "default" && (
        <p className="text-xs text-slate-500">{t(`${p}.defaultRegime`)}</p>
      )}
      {reports.length > 0 ? (
        <ul className="space-y-2">
          {reports.map((r) => (
            <StageRow
              key={r.id}
              report={r}
              canRecord={canRecord}
              findingId={findingId}
            />
          ))}
        </ul>
      ) : (
        <p className="text-xs text-slate-500">{t(`${p}.none`)}</p>
      )}
      {canRecord && (
        <div className="flex flex-wrap gap-2">
          {(["initial", "update", "final"] as const)
            .filter((s) => s === "update" || !has(s))
            .map((s) => (
              <button
                key={s}
                type="button"
                data-testid={`incident-report-open-${s}`}
                disabled={open.isPending}
                onClick={() => open.mutate(s)}
                className="rounded border border-brand-brass/40 bg-brand-brass/5 px-2 py-0.5 text-xs font-medium text-brand-brassDark hover:bg-brand-brass/10 disabled:opacity-50"
              >
                {t(`${p}.open.${s}`)}
              </button>
            ))}
        </div>
      )}
      {data.profile && (
        <p className="text-[11px] text-slate-400">{data.profile.citation}</p>
      )}
    </section>
  );
}
